import { useMemo, useState } from "react"
import {
  LAYER_CATEGORIES,
  LAYERS,
  layerMatchesSearch,
  layersByCategory,
  type LayerDef,
} from "./layers"

interface Props {
  enabled: Record<string, boolean>
  counts: Record<string, number>
  onToggle: (id: string) => void
  severityMin: 0 | 1 | 2 | 3
  onSeverityMinChange: (v: 0 | 1 | 2 | 3) => void
  ownedOnly: boolean
  onOwnedOnlyChange: (v: boolean) => void
}

export function LayerPanel(props: Props) {
  const [q, setQ] = useState("")

  const visible = useMemo(() => LAYERS.filter((l) => layerMatchesSearch(l, q)), [q])
  const byCat = useMemo(() => layersByCategory(visible), [visible])

  return (
    <div className="flex flex-col h-full text-[color:var(--text)]">
      <div className="px-3 py-3 border-b border-[color:var(--border)] flex flex-col gap-2">
        <input
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search layers…"
          className="search-input"
        />
        <div>
          <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)] mb-1">
            Incident severity floor
          </div>
          <div className="flex gap-1">
            {[
              { k: 0, l: "all" },
              { k: 1, l: "med+" },
              { k: 2, l: "high+" },
              { k: 3, l: "crit" },
            ].map(({ k, l }) => (
              <button
                key={k}
                type="button"
                aria-pressed={props.severityMin === k}
                onClick={() => props.onSeverityMinChange(k as 0 | 1 | 2 | 3)}
                className="btn-pill flex-1"
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 cursor-pointer mt-1">
          <input
            type="checkbox"
            checked={props.ownedOnly}
            onChange={(e) => props.onOwnedOnlyChange(e.target.checked)}
            className="accent-[color:var(--accent)]"
          />
          <span className="text-xs text-[color:var(--text)]">Owned only (★)</span>
        </label>
      </div>

      <div className="flex-1 overflow-y-auto px-1 py-2">
        {LAYER_CATEGORIES.map((cat) => {
          const items = byCat[cat]
          if (!items || items.length === 0) return null
          return (
            <div key={cat} className="mb-1">
              <div className="category-heading">{cat}</div>
              <div>
                {items.map((l) => (
                  <LayerRow
                    key={l.id}
                    def={l}
                    enabled={props.enabled[l.id] ?? false}
                    count={props.counts[l.id] ?? 0}
                    onToggle={() => props.onToggle(l.id)}
                  />
                ))}
              </div>
            </div>
          )
        })}

        {visible.length === 0 && (
          <div className="p-4 text-xs text-[color:var(--text-dim)] italic">
            no layers match “{q}”
          </div>
        )}
      </div>

      <div className="px-3 py-2 border-t border-[color:var(--border)] text-[10px] text-[color:var(--text-dim)] leading-relaxed">
        design inspired by{" "}
        <a
          href="https://github.com/koala73/worldmonitor"
          target="_blank"
          rel="noreferrer"
          className="text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
        >
          world monitor
        </a>{" "}
        · AGPL-3.0
      </div>
    </div>
  )
}

function LayerRow({
  def,
  enabled,
  count,
  onToggle,
}: {
  def: LayerDef
  enabled: boolean
  count: number
  onToggle: () => void
}) {
  // `kind: null` = client-only layer (still toggleable).
  // `kind: "future"` = coming-soon, toggle disabled.
  const locked = def.kind === "future" || !!def.locked
  return (
    <label
      className="layer-toggle group"
      data-locked={locked ? "true" : undefined}
      title={def.note ?? ""}
    >
      <input
        type="checkbox"
        checked={enabled}
        onChange={onToggle}
        disabled={locked}
      />
      <span className="layer-icon" style={{ color: def.color }}>
        {def.icon}
      </span>
      <span className="layer-label">{def.label}</span>
      {locked ? (
        <span className="layer-count text-[9px] italic">soon</span>
      ) : (
        <span className="layer-count">{count.toLocaleString()}</span>
      )}
    </label>
  )
}
